export const PEOPLE_BRIEFING = {
  greeting: {
    date: 'Monday, Jan 27',
    meetings: 3,
    tasks: 4,
    peopleAlerts: 2,
    lucMessage: "Hey Riya, busy start to the week! Tali flagged that engineering capacity is at 92% and two team leads submitted PTO requests for the same sprint. A couple of hiring decisions need your sign-off today."
  },
  peopleItems: [
    {
      id: 'pi1',
      title: 'Engineering capacity at critical threshold',
      agent: 'tali',
      category: 'capacity',
      alert: true,
      description: 'Team at 92% capacity with 3 projects queued for Q2. Tali recommends fast-tracking the Senior Developer hire.',
      expanded: {
        summary: 'Engineering team has been running above 85% capacity for 4 consecutive sprints. With 3 new projects queued for Q2, throughput is at risk without additional headcount.',
        details: [
          { label: 'Current Capacity', value: '92% (critical threshold)' },
          { label: 'Sprint Velocity Trend', value: 'Down 15% over 8 weeks' },
          { label: 'Queued Projects', value: '3 for Q2' },
          { label: 'Open Roles', value: '1 (Senior Developer)' },
          { label: 'Time to Hire (est)', value: '4-6 weeks' },
        ],
        thinking: 'Tali analyzed sprint velocity trends and capacity data. The team has been compensating with overtime, which correlates with the uptick in PTO requests. Hiring is the sustainable fix — but a temporary contractor could bridge the gap for Q2 kick-off.',
        proposedActions: [
          { label: 'Fast-track Senior Developer requisition', agent: 'Tali' },
          { label: 'Explore contractor for Q2 bridge', agent: 'Tali' },
          { label: 'Flag capacity risk to Arjun', agent: 'Luc' },
        ]
      }
    },
    {
      id: 'pi2',
      title: 'Overlapping PTO requests — Sprint 14',
      agent: 'tali',
      category: 'scheduling',
      alert: true,
      description: 'Two engineering leads requested time off during Sprint 14 (Feb 10-21). This would leave the team without senior coverage.',
      expanded: {
        summary: 'Both Raj (Backend Lead) and Meera (Frontend Lead) have requested PTO for Sprint 14. With no senior engineers available, sprint delivery is at risk.',
        details: [
          { label: 'Requesters', value: 'Raj Nair (Backend), Meera Rao (Frontend)' },
          { label: 'Sprint', value: 'Sprint 14 (Feb 10-21)' },
          { label: 'Impact', value: 'No senior engineering coverage' },
          { label: 'Sprint Deliverables', value: '2 critical features, 1 hotfix' },
        ],
        thinking: 'Tali checked the sprint plan and team calendar. Approving both would leave 3 junior developers without senior review capability. Recommends approving one and suggesting the other shift by one week — Raj has more flexibility based on his PTO history.',
        proposedActions: [
          { label: 'Approve Meera\'s PTO (lower flexibility)', agent: 'Tali' },
          { label: 'Suggest Raj shift to Sprint 15', agent: 'Tali' },
          { label: 'Arrange peer review coverage', agent: 'Tali' },
        ]
      }
    },
    {
      id: 'pi3',
      title: 'New hire onboarding — Alex Rivera starts Wednesday',
      agent: 'tali',
      category: 'onboarding',
      alert: false,
      description: 'Product Designer joining the marketing team. Onboarding plan ready, buddy assigned.',
      expanded: {
        summary: 'Alex Rivera (Product Designer) starts Wednesday. Tali has set up the full onboarding workflow — equipment, accounts, buddy system, and first-week schedule.',
        details: [
          { label: 'New Hire', value: 'Alex Rivera (Product Designer)' },
          { label: 'Team', value: 'Marketing' },
          { label: 'Start Date', value: 'Wednesday, Jan 29' },
          { label: 'Buddy', value: 'Neha (Senior Designer)' },
          { label: 'Equipment', value: 'Shipped, arriving Tuesday' },
          { label: 'Accounts', value: 'All provisioned' },
        ],
        thinking: 'Tali has followed the standard onboarding checklist. Early buddy pairing (day 1 vs day 3) increases 30-day satisfaction scores by 18% based on our data.'
      }
    }
  ],
  tasks: {
    today: [
      {
        id: 'pt1',
        title: 'Approve Senior Developer requisition',
        agent: 'tali',
        urgency: 'time-sensitive',
        description: 'Arjun pre-approved the budget. Tali needs your sign-off to post the job listing today.',
        expanded: {
          summary: 'The Senior Developer role has been drafted and approved by Arjun. Salary range: $135K-$155K. Tali estimates first interviews within 2 weeks of posting.',
          details: [
            { label: 'Role', value: 'Senior Developer (Full-stack)' },
            { label: 'Salary Range', value: '$135,000 - $155,000' },
            { label: 'Budget Status', value: 'Pre-approved by CEO' },
            { label: 'Time to First Interview', value: '~2 weeks' },
          ],
          artifacts: [
            { name: 'Job Description', type: 'pdf' },
            { name: 'Compensation Benchmark', type: 'spreadsheet' },
          ],
          thinking: 'Tali benchmarked the salary against market data — the range is competitive for the Bay Area market. Wright has reviewed the JD for employer brand consistency.'
        }
      },
      {
        id: 'pt2',
        title: 'Review candidate shortlist — Marketing Analyst',
        agent: 'tali',
        urgency: 'due-today',
        description: 'Tali screened 42 applications down to 5 candidates. Need your review to move to phone screens.',
        expanded: {
          summary: 'Tali applied scoring criteria to 42 applications for the Marketing Analyst role. Top 5 candidates all meet the minimum bar — 2 are strong matches.',
          details: [
            { label: 'Applications', value: '42 received' },
            { label: 'Shortlisted', value: '5 candidates' },
            { label: 'Strong Matches', value: '2 (score > 85/100)' },
            { label: 'Next Step', value: 'Phone screens this week' },
          ],
          artifacts: [
            { name: 'Candidate Scorecard', type: 'spreadsheet' },
            { name: 'Top 5 Profiles', type: 'pdf' },
          ]
        }
      },
      {
        id: 'pt3',
        title: 'Resolve PTO scheduling conflict',
        agent: 'tali',
        description: 'Review Tali\'s recommendation on the overlapping engineering PTO requests for Sprint 14.',
        expanded: {
          summary: 'Tali recommends approving Meera\'s request and asking Raj to shift by one week. Draft messages prepared for both.',
          artifacts: [
            { name: 'PTO Calendar View', type: 'image' },
          ]
        }
      }
    ],
    ifTime: [
      { id: 'pt4', title: 'Review engagement survey draft', agent: 'tali', description: 'Tali prepared the Q1 pulse survey. 12 questions, estimated 5-minute completion. Ready for your review.' },
      { id: 'pt5', title: 'Update team org chart', agent: 'tali', description: 'Tali flagged 3 reporting changes that need to be reflected in the org chart after last month\'s restructure.' }
    ]
  },
  peopleMetrics: {
    snapshot: [
      { label: 'Headcount', value: '47', trend: '+2' },
      { label: 'Open Roles', value: '3' },
      { label: 'Avg Time to Hire', value: '32 days', trend: '-4 days' },
      { label: 'Engagement Score', value: '4.2 / 5', trend: '+0.1' },
      { label: 'Voluntary Attrition', value: '8%', trend: '-2%' },
    ],
    alert: {
      id: 'pal1',
      title: 'Engineering team burnout risk detected',
      agents: ['tali', 'stark'],
      severity: 'high',
      description: '92% capacity for 4 sprints, PTO requests up 35%, and sprint velocity declining. Tali recommends intervention.',
      expanded: {
        details: [
          { label: 'Capacity Duration', value: '4 sprints above 85%' },
          { label: 'PTO Requests', value: 'Up 35% vs last quarter' },
          { label: 'Velocity Decline', value: '15% over 8 weeks' },
          { label: 'Affected Team', value: 'Engineering (12 people)' },
          { label: 'Risk Level', value: 'High — attrition risk if unaddressed' },
        ],
        thinking: 'Tali and Stark correlated capacity data, PTO patterns, and sprint velocity. The pattern matches pre-attrition signals seen in 2 previous cases. The Senior Developer hire and temporary workload redistribution are the recommended interventions.',
        proposedActions: [
          { label: 'Expedite Senior Developer hire', agent: 'Tali' },
          { label: 'Redistribute Sprint 14 workload', agent: 'Tali' },
          { label: 'Schedule 1-on-1s with engineering leads', agent: 'Tali' },
        ]
      }
    }
  },
  meetings: [
    { id: 'pm1', title: 'HR weekly sync', time: '9:30 AM', duration: '30 min', with: 'HR Team', type: 'internal', agenda: ['Open roles status', 'PTO conflict resolution', 'Onboarding prep for Alex'] },
    { id: 'pm2', title: 'Engineering capacity check-in', time: '11:00 AM', duration: '30 min', with: 'Engineering Leads', type: 'internal', agenda: ['Sprint 14 planning concerns', 'Capacity and velocity trends', 'Hiring timeline update'] },
    { id: 'pm3', title: 'Candidate debrief — Marketing Analyst', time: '2:30 PM', duration: '30 min', with: 'Priya Sharma (Marketing)', type: 'internal', agenda: ['Review top 5 shortlist', 'Phone screen scheduling', 'Role expectations alignment'] },
  ],
  dailyTimeline: [
    { time: '9:00 AM', type: 'task', title: 'Approve Senior Developer requisition', agent: 'tali' },
    { time: '9:30 AM', type: 'meeting', title: 'HR weekly sync', duration: '30 min', with: 'HR Team' },
    { time: '10:00 AM', type: 'task', title: 'Review candidate shortlist', agent: 'tali' },
    { time: '10:30 AM', type: 'task', title: 'Resolve PTO scheduling conflict', agent: 'tali' },
    { time: '11:00 AM', type: 'meeting', title: 'Engineering capacity check-in', duration: '30 min', with: 'Engineering Leads' },
    { time: '1:00 PM', type: 'task', title: 'Review Alex Rivera onboarding plan', agent: 'tali' },
    { time: '2:30 PM', type: 'meeting', title: 'Candidate debrief — Marketing Analyst', duration: '30 min', with: 'Priya Sharma' },
  ],
  inbox: [
    { id: 'pei1', sender: 'Raj Nair (Backend Lead)', senderInitials: 'RN', time: '8:15 AM', summary: 'Requesting PTO for Sprint 14 — acknowledges overlap with Meera\'s request.', expandedSummary: 'Raj submitted his PTO request for Feb 10-21 and is aware Meera also requested the same sprint. He says he has some flexibility — could shift to Feb 17-28 if needed, but prefers the original dates for a family trip. He\'s asking for a decision by Wednesday so he can book flights.' },
    { id: 'pei2', sender: 'Priya Sharma (Marketing)', senderInitials: 'PS', time: '7:50 AM', summary: 'Confirming Alex Rivera\'s start details — has a desk setup question.', expandedSummary: 'Priya wants to confirm Alex\'s desk will be near the design team (2nd floor, east wing). She\'s also asking if the team welcome lunch is still planned for Wednesday and whether she should send the team introduction email today or wait until Alex\'s first day. Minor logistics but wants to make a great first impression.' },
    { id: 'pei3', sender: 'Arjun Mehta (CEO)', senderInitials: 'AM', time: '7:30 AM', summary: 'Asking for engineering team pulse — concerned about capacity and velocity trends.', expandedSummary: 'Arjun saw the capacity data in his morning brief and wants more context. He\'s asking: is this a temporary spike or a structural issue, what\'s the team sentiment like in recent 1-on-1s, and should he be worried about attrition? He wants to discuss during a quick call before his 11 AM engineering check-in.' },
    { id: 'pei4', sender: 'Neha Gupta (Senior Designer)', senderInitials: 'NG', time: 'Yesterday', summary: 'Volunteering to be Alex Rivera\'s onboarding buddy — confirming availability.', expandedSummary: 'Neha confirmed she\'s available and excited to be Alex\'s buddy. She\'s reviewed the onboarding plan and has one suggestion: adding a 30-minute "tools walkthrough" on day 1 covering Figma workspace setup and the design system. She\'s prepared a welcome kit with team norms and project context docs.' },
    { id: 'pei5', sender: 'HR Software (BambooHR)', senderInitials: 'HR', time: 'Yesterday', summary: 'Q1 engagement survey template ready — 12 questions, needs admin approval to schedule.', expandedSummary: 'The Q1 pulse survey template has been finalized in BambooHR. 12 questions covering satisfaction, workload, management effectiveness, and growth opportunities. Estimated 5-minute completion time. It needs admin approval to schedule the send date — Tali recommends next Monday to avoid end-of-month crunch.' },
  ]
};
